import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbaseClient';
import { formatCurrency } from '../../lib/utils';
import { 
  Eye, Building2, CheckCircle, Circle, Clock, 
  AlertCircle, XCircle, Search 
} from 'lucide-react';

export default function PartnerProjects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  // --- Fetch Partner's Assigned Sites -------------------------
  useEffect(() => {
    const fetchPartnerProjects = async () => {
      setLoading(true);
      try {
        // 1. Get partner
        const partners = await pb.collection('partners').getFullList({
          filter: `createdBy = "${user.id}" || email = "${user.email}"`,
        });
        if (!partners.length) {
          setProjects([]);
          setLoading(false);
          return;
        }
        const partner = partners[0];

        // 2. Get assigned site IDs
        const assignments = await pb.collection('partner_site_assignments').getFullList({
          filter: `partner = "${partner.id}" && isActive = true`,
          expand: 'site',
        });
        const siteIds = assignments.map(a => a.expand?.site?.id).filter(Boolean);

        if (!siteIds.length) {
          setProjects([]);
          setLoading(false);
          return;
        }

        // 3. Fetch sites (accounts_projects) with client expand
        const records = await pb.collection('accounts_projects').getFullList({
          filter: siteIds.map(id => `id="${id}"`).join('||'),
          expand: 'clientId',
          sort: '-created',
        });

        // 4. Get financial data for these sites
        const [expenses, vendorPayments, clientPayments] = await Promise.all([
          pb.collection('project_expenses').getFullList({
            filter: `project ~ "${siteIds.join('||')}"`,
          }),
          pb.collection('vendor_payments').getFullList({
            filter: `project ~ "${siteIds.join('||')}"`,
          }),
          pb.collection('client_payments').getFullList({
            filter: `project ~ "${siteIds.join('||')}"`,
          }),
        ]);

        // 5. Aggregate totals per project
        const projectTotals = {};
        expenses.forEach(e => {
          if (!projectTotals[e.project]) projectTotals[e.project] = { cost: 0, revenue: 0 };
          projectTotals[e.project].cost += e.amount || 0;
        });
        vendorPayments.forEach(v => {
          if (!projectTotals[v.project]) projectTotals[v.project] = { cost: 0, revenue: 0 };
          projectTotals[v.project].cost += v.amount || 0;
        });
        clientPayments.forEach(c => {
          if (!projectTotals[c.project]) projectTotals[c.project] = { cost: 0, revenue: 0 };
          projectTotals[c.project].revenue += c.amount || 0;
        });

        // 6. Enrich projects
        const projectsWithFinancials = records.map(p => {
          const totals = projectTotals[p.id] || { cost: 0, revenue: 0 };
          return {
            ...p,
            totalCost: totals.cost,
            revenue: totals.revenue,
            profit: totals.revenue - totals.cost,
            clientName: p.expand?.clientId?.name || 'N/A',
          };
        });

        setProjects(projectsWithFinancials);
      } catch (err) {
        console.error('Error fetching partner projects:', err);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchPartnerProjects();
  }, [user]);

  // --- Helpers --------------------------------------------------
  const getStatusBadge = (status) => {
    const map = {
      'upcoming': { label: 'Upcoming', variant: 'info', icon: Clock },
      'ongoing': { label: 'Ongoing', variant: 'success', icon: Circle },
      'completed': { label: 'Completed', variant: 'success', icon: CheckCircle },
      'on-hold': { label: 'On Hold', variant: 'warning', icon: AlertCircle },
      'active': { label: 'Active', variant: 'success', icon: Circle },
      'planning': { label: 'Planning', variant: 'info', icon: Clock },
    };
    const s = map[status] || { label: status || 'N/A', variant: 'default', icon: null };
    const Icon = s.icon;
    return (
      <Badge variant={s.variant} className="flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {s.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    if (type === 'construction') return <Badge variant="info">Construction</Badge>;
    if (type === 'interior') return <Badge variant="purple">Interior</Badge>;
    return <Badge>{type || 'N/A'}</Badge>;
  };

  // --- Filters --------------------------------------------------
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name?.toLowerCase().includes(search.toLowerCase()) ||
                          project.clientName?.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === 'All' || project.type === filterType;
    const matchesStatus = filterStatus === 'All' || project.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  // --- Stats ----------------------------------------------------
  const totalProjects = projects.length;
  const ongoing = projects.filter(p => p.status === 'ongoing' || p.status === 'active').length;
  const completed = projects.filter(p => p.status === 'completed').length;
  const upcoming = projects.filter(p => p.status === 'upcoming' || p.status === 'planning').length;

  // --- Loading --------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading your projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* --- Header ---------------------------------------------- */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-amber-500" />
            My Projects
          </h1>
          <p className="text-sm text-slate-500">Sites assigned to you � view only</p>
        </div>
        <div className="text-xs text-slate-400 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
          ?? Read-only
        </div>
      </div>

      {/* --- Stats Cards ------------------------------------------ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Total Sites</p>
            <p className="text-2xl font-bold text-slate-800">{totalProjects}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Ongoing / Active</p>
            <p className="text-2xl font-bold text-emerald-600">{ongoing}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Upcoming / Planning</p>
            <p className="text-2xl font-bold text-amber-600">{upcoming}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Completed</p>
            <p className="text-2xl font-bold text-purple-600">{completed}</p>
          </CardContent>
        </Card>
      </div>

      {/* --- Filters ---------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full"
            />
          </div>
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <option value="All">All Types</option>
          <option value="construction">Construction</option>
          <option value="interior">Interior</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <option value="All">All Status</option>
          <option value="upcoming">Upcoming</option>
          <option value="ongoing">Ongoing</option>
          <option value="completed">Completed</option>
          <option value="on-hold">On Hold</option>
          <option value="planning">Planning</option>
          <option value="active">Active</option>
        </select>
        <Button
          variant="outline"
          onClick={() => { setSearch(''); setFilterType('All'); setFilterStatus('All'); }}
        >
          Clear Filters
        </Button>
      </div>

      {/* --- Table -------------------------------------------------- */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-700">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Site Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Client</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Work Order</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Cost</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Revenue</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">P/L</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-slate-500">
                    No projects assigned to you yet.
                  </td>
                </tr>
              ) : (
                filteredProjects.map((project) => (
                  <tr key={project.id} className="hover:bg-slate-50/50 transition">
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {project.name}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{project.clientName}</td>
                    <td className="px-4 py-3">{getTypeBadge(project.type)}</td>
                    <td className="px-4 py-3">{getStatusBadge(project.status)}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatCurrency(project.workOrderValue || 0)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-600">{formatCurrency(project.totalCost || 0)}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatCurrency(project.revenue || 0)}</td>
                    <td className={`px-4 py-3 text-right font-bold ${(project.profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {(project.profit || 0) >= 0 ? '+' : ''}{formatCurrency(project.profit || 0)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Link to={`/partner/projects/${project.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:bg-blue-50 border-blue-200 gap-1"
                        >
                          <Eye className="w-4 h-4" /> View
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400 text-center">
        Showing {filteredProjects.length} of {projects.length} projects
      </p>
    </div>
  );
}